import * as EDT from '../../Type/EnhancedData'

export const MANAGE_ENHANCED_DATA_PAGE_ROUTE = '/enhanceddatatransactionmanage.pay'
export const MAX_NO_OF_LINE_ITEMS = 301

// Product Search and Inifinite Scroll behavior controlling fields
export const WAIT_TIME_FOR_CHANGE_DETECTION = 1000
export const SEARCH_PRODUCT_LIMIT = 50
export const SEARCH_PRODUCT_MAX_ITEMS = 250
export const SCROLL_BOTTOM_BUFFER_PX = 80

// Scroller configs
export const ERROR_BANNER_SCROLL_THRESHOLD = 10 // number of errors to enable scrolling to error banner
export const TABLE_ITEM_SCROLL_THRESHOLD = 10 // number of line items to enable scrolling to error banner

// Static messages
export const INLINE_PRODUCT_FORM_ERR_BANNER_MSG = 'Please correct the above errors before proceeding.'
export const NETWORK_ERROR_BANNER_MESSAGE = 'Unable to load page. Please try again. If the issue persists, please contact PayTrace at 888-806-6545.'
export const CUSTOM_PRODUCT_NOT_FOUND_MSG = 'This product does not exist in your custom product list. Please create a new product record by clicking the Create New Product link or delete this item.'

// DOM Element IDs
export const PLUS_CREATE_NEW_PRODUCT_ITEM_ELEM_ID = 'plus-create-new-product-item'

export const defaultMEDFormStateData: EDT.MEDFormStateData = {
  customerReferenceId: '',
  customerTaxId: '',
  sourceZipCode: '',
  shippingCountry: '',
  shippingZipCode: '',
  merchantTaxId: '',
  invoice: '',
  taxAmount: 0,
  discountAmount: 0,
  freightAmount: 0,
  surchargeAmount: 0,
  surchargeTaxAmount: 0,
  lineItemMap: {},
}

export const ED_FIELD_SPEC: Record<string, any> = {
  level2: {
    customerReferenceId: {
      displayName: 'Customer Reference ID',
      tooltip: 'Enter the customer-provided reference value, often a cost center code or PO number. Cannot be blank. Up to 50 alphanumeric, spaces and special characters allowed.',
      pattern: '^[ !-~]+$',
      errorMessage: 'Customer Reference ID is required for L2/L3 transactions; can be 1 - 50 alphanumeric, spaces and special characters',
      maxLength: 50,
      minLength: 1,
    },
    sourceZipCode: {
      displayName: 'Source Zip Code',
      tooltip: 'Enter the zip code of the “ship-from” address.',
      pattern: '.*',
      errorMessage: 'Source Postal Code is required for L3 transactions;  US postal codes are 5 numeric characters or 10 numeric characters with a dash.  Note: Other countries must adhere to their designated country-specific formatting rules.',
      maxLength: 10,
      minLength: 5,
    },
    shippingCountry: {
      displayName: 'Shipping Country',
      tooltip: 'Enter the country of destination address where goods are being sent. 2-digit ISO Country Code.',
      pattern: '^[A-Z]{0,20}$',
      errorMessage: 'Shipping Country is required for L3 transaction and must be formatted correctly; 2-digit ISO-3166-2 Country Code',
      maxLength: 2,
      minLength: 0,
    },
    shippingZipCode: {
      displayName: 'Shipping Zip Code',
      tooltip: 'Enter the zip code of the “ship-from” address.',
      pattern: '.*',
      errorMessage: 'Shipping Zip Code is required for L3 transactions; US postal codes are 5 numeric characters or 10 numeric characters with a dash.  Note: Other countries must adhere to their designated country-specific formatting rules.',
      maxLength: 10,
      minLength: 5,
    },
    invoice: {
      displayName: 'Invoice',
      tooltip: 'Enter a unique identifier for the transaction; often used in accounting or inventory systems. Up to 50 characters of free form text, spaces are acceptable.  PayTrace will truncate this value according to each processor’s specifications.',
      pattern: '.*',
      errorMessage: 'Invoice number is required for L2/L3 transactions; can be up to 50 characters free form text.',
      maxLength: 50,
      minLength: 0,
    },
    merchantTaxId: {
      displayName: 'Merchant Tax ID',
      tooltip: 'Enter the merchant’s tax identifier used for tax reporting purposes. 1-20 alphanumeric characters, no spaces. If not provided, PayTrace will use the data from the merchant settings or the Merchant ID.',
      pattern: '^[a-zA-Z0-9]*$',
      errorMessage: 'Merchant Tax ID is required; 1-20 alphanumeric characters, no spaces.',
      maxLength: 20,
      minLength: 0,
    },
    customerTaxId: {
      displayName: 'Customer Tax ID',
      tooltip: 'Enter the customer’s tax identifier used for tax reporting purposes. 1-20 alphanumeric characters, no spaces. Conditionally required for tax exempt transactions. If not provided, PayTrace will use data on file.',
      pattern: '^[a-zA-Z0-9]*$',
      errorMessage: 'Customer Tax ID is required; 1-20 alphanumeric characters, no spaces.',
      maxLength: 20,
      minLength: 0,
    },
  },
  level3: {
    taxAmount: {
      displayName: 'Tax Amount',
      tooltip: 'Enter the total tax amount for the transaction (equal to the sum of all line item total amounts if L3). Cannot be </= the transaction total amount.',
      pattern: '^\\$?([0-9]{0,17}|([0-9]{0,15}\\.[0-9]{0,2}))$',
      errorMessage: 'Tax Amount is required for L2/L3 transactions; up to 12 digits and a decimal, dash allowed, cannot be >/= transaction amount, cannot be a negative value.',
      maxLength: 12,
      minLength: 0,
    },
    discountAmount: {
      displayName: 'Discount',
      tooltip: 'Enter the total amount discounted from the total transaction amount.',
      pattern: '^\\$?([0-9]{0,17}|([0-9]{0,15}\\.[0-9]{0,2}))$',
      errorMessage: 'Discount is required for L3 transactions; cannot be >/= transaction amount, cannot be a negative value, up to 12 numeric digits and a decimal.',
      maxLength: 12,
    },
    freightAmount: {
      displayName: 'Freight/Shipping',
      tooltip: 'Enter the portion of the total transaction amount generated from shipping costs.',
      pattern: '^\\$?([0-9]{0,17}|([0-9]{0,15}\\.[0-9]{0,2}))$',
      errorMessage: 'Freight/Shipping is required for L3 transactions; cannot be >/= transaction amount, cannot be a negative value, up to 12 numeric digits and a decimal.',
      maxLength: 12,
    },
    surchargeAmount: {
      displayName: 'Surcharge',
      tooltip: 'Enter additional fee applied to cover specific processing or operational costs.',
      pattern: '^\\$?([0-9]{0,17}|([0-9]{0,15}\\.[0-9]{0,2}))$',
      errorMessage: 'Surcharge is required for L3 transactions; cannot be >/= transaction amount, cannot be a negative value, up to 12 numeric digits and a decimal.',
      maxLength: 12,
    },
    surchargeTaxAmount: {
      displayName: 'Surcharge Tax',
      tooltip: 'Enter the surcharge tax rate percentage applied to the total.',
      pattern: '^\\$?([0-9]{0,17}|([0-9]{0,15}\\.[0-9]{0,2}))$',
      errorMessage: 'Surcharge tax must be formatted correctly; up to 12 digits and a decimal, cannot be >/= surcharge amount, cannot be a negative value.',
      maxLength: 12,
    },
  },
  line_item: {
    productName: {
      displayName: 'Product',
      tooltip: 'Enter the description of goods or services being sold.',
      pattern: '^(?!0+$).+',
      errorMessage: 'The Product is required for L3 transactions; must be 2-35 characters free-form text, cannot be null or zeros, cannot match the Merchant Name for the transaction.',
      maxLength: 35,
      minLength: 2,
    },
    quantity: {
      displayName: 'Quantity',
      pattern: '^\\d{0,10}(\\.\\d{0,2})?$',
      errorMessage: 'Quantity is required for L3 transactions; up to 12 numeric characters and a decimal, cannot be a negative value.',
      maxLength: 12,
      minLength: 0,
    },
    unitOfMeasure: {
      displayName: 'Unit of Measure',
      tooltip: 'Enter the unit the product or service is measured in (each, boxes, gallons, hours, etc.)',
      pattern: '^(?!0+$).+',
      errorMessage: 'Unit of Measure is required for L3 transactions; can be 1-12 AN characters, cannot be null or all zeros.',
      maxLength: 12,
      minLength: 1,
    },
    costPerUnit: {
      displayName: 'Cost Per Unit',
      tooltip: 'Enter the cost of one unit of goods or services being sold.',
      pattern: '^\\d{0,10}(\\.\\d{0,2})?$',
      errorMessage: 'Unit Cost is required for L3 transactions; cannot be >/= the transaction amount, cannot be a negative value, can be up to 12 numeric digits and a decimal.',
      maxLength: 12,
      minLength: 0,
    },
    itemTax: {
      displayName: 'Item Tax',
      tooltip: 'Enter a tax amount for each line item. The sum of all line item tax amounts must equal the tax amount for the overall transaction.',
      pattern: '^\\d{0,10}(\\.\\d{0,2})?$',
      errorMessage: 'Item Tax is required for L3 transactions; cannot be >/= transaction amount, cannot be a negative value, can be up to 12 numeric digits and a decimal.',
      maxLength: 12,
      minLength: 0,
    },
    amount: {
      displayName: 'Amount',
      tooltip: 'The pre-tax total amount of the line item.',
      pattern: '^\\d{0,10}(\\.\\d{0,2})?$',
      errorMessage: 'Item Total is required for L3 transactions; cannot be a negative amount, cannot > transaction amount, can be up to 12 numeric digits and a decimal, must = (Quantity * Unit Cost) - Line Item Discount Amount',
      maxLength: 12,
      minLength: 0,
    },
    productCode: {
      displayName: 'Product Code',
      tooltip: 'Unique identifier for the product often used for inventory and invoicing.',
      pattern: '.*',
      errorMessage: '',
      maxLength: 20,
      minLength: 0,
    },
    commodityCode: {
      displayName: 'Commodity Code',
      tooltip: 'Code used for government reporting or classification. It must be between 3 to 12 alphanumeric characters. No special characters are allowed.',
      pattern: '^[a-zA-Z0-9\\s-]+$',
      errorMessage: 'Commodity Code is required for L2/L3 transactions; must be 3-20 AN characters, dashes allowed.',
      maxLength: 20,
      minLength: 3,
    },
    itemDiscountAmount: {
      displayName: 'Discount',
      tooltip: 'Enter the amount discounted from the line item.',
      pattern: '^\\d{0,10}(\\.\\d{0,2})?$',
      errorMessage: 'Discount cannot be >/= the line item amount, cannot be a negative value, can be up to 12 numeric digits and a decimal.',
      maxLength: 12,
      minLength: 0,
    },
  },
  settings: {
    merchantTaxId: {
      tooltip: 'Please provide your business Tax ID. The merchant tax ID may contain up to 20 alphanumeric characters.',
      pattern: '^[a-zA-Z0-9]*$',
      errorMessage: 'Merchant Tax ID can only contain alphanumeric characters.',
      maxLength: 20,
      minLength: 0,
    },
  },
}

export const color = {
  RED: '#cc5a44',
}

export const MSG_L3_ENHANCED_DATA_SUCCESS = 'This transaction is now eligible to qualify for better processing rates.'
export const MSG_L2_ENHANCED_DATA_SUCCESS = 'Your enhanced data updates have been saved.'
export const emptyEnhancedDataBackendError: EDT.EnhancedDataBackendError = {
  otherErrors: [],
  fieldErrorMap: {},
  lineItemErrors: {},
}

export const emptyEnhancedDataValidationError: EDT.EnhancedDataValidationError = {
  inputErrors: {},
  lineItemErrors: {},
}

export const messageItems = {
  taxIdInfo: 'Please provide your business Tax ID. The merchant tax ID may contain up to 20 numeric characters.',
  noProductsAdded: 'No product items have been added.',
  noProductsFound: 'No products were found.',
  deleteConfirmation: 'Are you sure you want to delete the product item?',
}

export const initialProductItem: EDT.LineItemStateData = ({
  productLabel: '',
  productCode: '',
  commodityCode: '',
  unitOfMeasure: '',
  costPerUnit: 0,
  quantity: 0,
  productName: '',
  amount: 0,
  itemTax: 0,
  index: -1,
  rowNumber: 1,
  itemDiscountAmount: 0,
})
